export interface MessageImagePlaceholder {
  /** The `image://{id}` handle from the attachment entry's `attachment_path`. */
  handle: string;
  /** The attachment entry's stored text — label, type, dimensions, size. */
  content: string;
}

export interface MessageImagePlaceholdersProps {
  placeholders: MessageImagePlaceholder[];
}

/**
 * Sent-message image placeholders, rendered from the persisted `attachment`
 * context entries — so they survive a reload and a second reader sees them.
 * No image data is persisted anywhere, so no pixels are fetched or rendered.
 */
export function MessageImagePlaceholders({ placeholders }: MessageImagePlaceholdersProps) {
  if (placeholders.length === 0) return null;

  return (
    <div className="mt-2 flex w-full flex-col gap-1.5" data-testid="message-images">
      {placeholders.map((placeholder) => (
        <div
          key={placeholder.handle}
          data-testid="message-image-placeholder"
          data-image-handle={placeholder.handle}
          className="flex items-start gap-2 rounded-md bg-muted px-2.5 py-1.5 text-xs text-muted-foreground"
        >
          <ImageGlyph />
          <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">
            {placeholder.content}
          </span>
        </div>
      ))}
    </div>
  );
}

function ImageGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mt-0.5 shrink-0"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-4.35-4.35a1 1 0 0 0-1.42 0L5 21" />
    </svg>
  );
}
